import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { replace } from 'connected-react-router';
import { connect } from 'react-redux';
import { isEqual, uniqBy } from 'lodash';
import moment from 'moment-timezone';

import Strings from '../../../Strings';
import DeviceInvoicesReviewTable from './DeviceInvoicesReviewTable';
import { Tabs, Tab } from '../../../components/Tabs';
import { DeviceInvoicesTabsDefinition, DeviceInvoicesTypes, extractErrorMessage, extractExceptionMessage, getGenerationSummaryMessage, INVOICE_STATUSES } from './helpers';
import { DOWNLOAD_DEVICE_INVOICE_INITIAL_RESULT, DOWNLOAD_DEVICE_INVOICE_RECURRING_RESULT, GENERATE_DEVICE_INVOICE_RECURRING_RESULT, GET_DEVICE_INVOICES_INITIAL_RESULT, GET_DEVICE_INVOICES_RECURRING_RESULT, invoiceActions, RECALCULATE_DEVICE_INVOICE_INITIAL_RESULT, RECALCULATE_DEVICE_INVOICE_RECURRING_RESULT } from './redux/actions';
import { notificationActions } from '../../../components/Notification/redux/actions';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { CareProviderFilter, dateFilter, getFiltersDefinition, MonthFilter, OrgFilter } from '../../../components/AdvancedFilters/FiltersDefinition';
import { GET_MY_ORGS_RESULT, actions as orgFilterActions } from '../../../components/OrgSelect/orgFilterActions';
import { TextInput } from '../../../components/PageHeader/TextInput';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import { DateFilters } from '../../../utils/DateFilters';
import { getRelevantFilters } from '../../../components/AdvancedFilters/helpers';
import DropdownMenu from '../../../components/DropdownMenu/DropdownMenu';
import { openModalAction } from '../../../actions/modal';
import { GenerateRecurringInvoicesModal } from './modals';
import { downloadFileOnly } from '../../../utils';
import { actions as organizationActions } from '../../Organizations/redux/actions';
import { GET_CARE_PROVIDERS_RESULT } from '../../Organizations/redux/constants';
import { useGlobalPageSize } from '../../../utils/tableUtils';
import { Input } from 'containers/Form';
import { isSuperUser, isShipper } from '../../../utils/userRoles';
import { getShowOnlyMyBillingOrgsPreference, setShowOnlyMyBillingOrgsPreference } from './deviceInvoicePreferences';

const defaultTabCounts = () => DeviceInvoicesTabsDefinition.reduce((acc, tab) => {
  acc[tab.key] = 0;
  return acc;
}, {});

const getDefaultSorting = (invoiceType, status) => {
  if (status !== INVOICE_STATUSES.action_required.key) {
    return [];
  }
  if (invoiceType === DeviceInvoicesTypes.RECURRING) {
    return [{ id: 'kit_id', desc: false }];
  }
  return [
    { id: 'device_id', desc: false },
    { id: 'shipment_date_from_rxcap', desc: true },
  ];
};

const toFilterByParam = (filters, currentUserBillingOrgs, showOnlyMyBillingOrgs) => {
  if (!filters?.length) {
    return undefined;
  }
  const filterBy = [];
  [
    'customer_id',
    'billing_org_id',
    'provider',
    'programCheckbox',
    'DeviceType',
  ].forEach(group => {
    const groupFilters = getRelevantFilters(filters.filter(f => f.group === group));
    if (groupFilters?.length > 0) {
      filterBy.push(`${group}:${groupFilters.map(f => f.value).join(',')}`);
    }
    if (group === 'billing_org_id' && groupFilters?.length === 0 && currentUserBillingOrgs?.length > 0 && showOnlyMyBillingOrgs) {
      filterBy.push(`${group}:${currentUserBillingOrgs.map(o => o.value).join(',')}`);
    }
  });
  return filterBy.length > 0 ? filterBy : undefined;
};

const getInvoiceDateRangeParams = filters => {
  const monthFilter = getRelevantFilters((filters || []).filter(f => f.group === 'month'));
  if (!monthFilter?.length) {
    return {};
  }
  const selectedMonth = monthFilter[0].state;
  if (!selectedMonth) {
    return {};
  }
  return {
    startTimestamp: selectedMonth.clone().startOf('month').format(),
    endTimestamp: selectedMonth.clone().endOf('month').format(),
  };
};

function DeviceInvoicesTabs(props) {
  const { invoiceType, refreshTimestamp, filters, authConfig } = props;
  const { getDeviceInvoiceInitialRows, getDeviceInvoiceRecurringRows, getOrgs, getCareProviders } = props;
  const { showNotification, replacePath, openConfirmModal, recalculateDeviceInvoiceInitial, recalculateDeviceInvoiceRecurring, generateDeviceInvoiceRecurring } = props;
  const { downloadDeviceInvoiceInitial, downloadDeviceInvoiceRecurring, openDateRangeSelectionModal } = props;
  
  const urlParams = new URLSearchParams(window.location.search);
  const statusFromUrl = urlParams.get('status');
  const globalPageSize = useGlobalPageSize();
  const dateFiltersForPage = { ...DateFilters };
  const invoiceDateFilter = useMemo(() => {
    const baseDateFilter = dateFilter(dateFiltersForPage);
    return {
      ...baseDateFilter,
      dateFilter: {
        ...baseDateFilter.dateFilter,
        text: Strings.deviceInvoices.invoiceDate || 'Invoice Date',
      },
    };
  }, [dateFiltersForPage]);

  const [status, setStatus] = useState(statusFromUrl || DeviceInvoicesTabsDefinition[0].key);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ offset: 0, limit: globalPageSize, totalRecords: 0 });
  const [tabCounts, setTabCounts] = useState(defaultTabCounts);
  const [sorting, setSorting] = useState(getDefaultSorting(invoiceType, status));
  const [isLoading, setIsLoading] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [careProviders, setCareProviders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showGenerateRecurringModal, setShowGenerateRecurringModal] = useState(false);
  const hasBillingOrgScope = Boolean(authConfig?.billing_org_ids?.length);
  const [showOnlyMyBillingOrgs, setShowOnlyMyBillingOrgs] = useState(() => (
    getShowOnlyMyBillingOrgsPreference(Boolean(authConfig?.billing_org_ids?.length))
  ));

  const isRecurring = invoiceType === DeviceInvoicesTypes.RECURRING;
  const canManageDeviceInvoices = isSuperUser() || isShipper();
  const searchTimeoutRef = useRef(null);
  const currentUserBillingOrgs = useMemo(() => {
    return orgs.filter(o => authConfig?.billing_org_ids?.includes(o.masked_id));
  }, [authConfig, orgs]);

  useEffect(() => {
    getOrgs().then(resp => {
      if (resp?.type === GET_MY_ORGS_RESULT) {
        setOrgs(
          resp.response?.data?.map(o => {
            return { ...o, value: o.masked_id, label: o.name };
          }),
        );
      }
    });
    getCareProviders().then(resp => {
      if (resp?.type === GET_CARE_PROVIDERS_RESULT) {
        setCareProviders(resp?.response ? uniqBy(resp.response, 'uuid') : []);
      }
    });
  }, [getCareProviders, getOrgs]);

  useEffect(() => {
    if (!hasBillingOrgScope && showOnlyMyBillingOrgs) {
      setShowOnlyMyBillingOrgs(false);
    }
  }, [hasBillingOrgScope, showOnlyMyBillingOrgs]);

  useEffect(() => {
    setShowOnlyMyBillingOrgsPreference(hasBillingOrgScope && showOnlyMyBillingOrgs);
  }, [hasBillingOrgScope, showOnlyMyBillingOrgs]);

  const loadData = useCallback(() => {
    const action = isRecurring ? getDeviceInvoiceRecurringRows : getDeviceInvoiceInitialRows;
    setIsLoading(true);
    const expectedType = isRecurring ? GET_DEVICE_INVOICES_RECURRING_RESULT : GET_DEVICE_INVOICES_INITIAL_RESULT;
    const activeStatus = status;
    const filterParams = {
      ...getInvoiceDateRangeParams(filters),
      ...(toFilterByParam(filters, currentUserBillingOrgs, showOnlyMyBillingOrgs) ? { filterBy: toFilterByParam(filters, currentUserBillingOrgs, showOnlyMyBillingOrgs) } : {}),
      ...(searchQuery ? { search: searchQuery } : {}),
    };
    const sortParams = sorting?.length > 0 ? {
      sortColumn: sorting[0].id,
      sortType: sorting[0].desc ? 'descending' : 'ascending',
    } : {};
    Promise.all(DeviceInvoicesTabsDefinition.map(tab => {
      const isActiveTab = tab.key === activeStatus;
      return action({
        status: tab.key,
        offset: isActiveTab ? pagination.offset : 0,
        limit: isActiveTab ? pagination.limit : 1,
        ...filterParams,
        ...(isActiveTab ? sortParams : {}),
      });
    }))
      .then(responses => {
        const failedResponse = responses.find(resp => resp.type !== expectedType);
        if (failedResponse) {
          showNotification(extractErrorMessage(failedResponse), 5000, true);
          setData([]);
          return;
        }
        const nextTabCounts = {};
        responses.forEach((resp, index) => {
          nextTabCounts[DeviceInvoicesTabsDefinition[index].key] = resp.response?.pagination?.totalRecords || 0;
        });
        const activeResponse = responses[DeviceInvoicesTabsDefinition.findIndex(tab => tab.key === activeStatus)]?.response;
        setTabCounts(nextTabCounts);
        setPagination(activeResponse?.pagination || { ...pagination, totalRecords: 0 });
        setData(Array.isArray(activeResponse?.data) ? activeResponse.data : []);
      })
      .catch(error => {
        showNotification(extractExceptionMessage(error), 5000, true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [filters, getDeviceInvoiceInitialRows, getDeviceInvoiceRecurringRows, isRecurring, pagination.limit, pagination.offset, searchQuery, showNotification, sorting, status, currentUserBillingOrgs, showOnlyMyBillingOrgs]);

  useEffect(() => {
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(loadData, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [loadData]);

  useEffect(() => {
    if (status) {
      const newUrlParams = new URLSearchParams(window.location.search);
      newUrlParams.set('status', status);
      replacePath(`${window.location.pathname}?${newUrlParams}`);
    }
  }, [refreshTimestamp, status, replacePath]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, offset: 0, limit: globalPageSize }));
  }, [filters, globalPageSize, searchQuery]);

  useEffect(() => {
    setData([]);
    setPagination(prev => ({ ...prev, offset: 0 }));
    setSorting(getDefaultSorting(invoiceType, status));
  }, [invoiceType, status]);

  const handleRecalculate = invoices => {
    const isBulkRecalculate = !invoices;
    const invoicesToProcess = invoices || [];
    const payload = {invoice_ids: invoicesToProcess.map(i => i.invoice_uuid)};

    const recalculateAction = isRecurring ? recalculateDeviceInvoiceRecurring : recalculateDeviceInvoiceInitial;
    let batchWarning = Strings.formatString(Strings.deviceInvoices.batchActionWarning, invoicesToProcess.length);
    if (isBulkRecalculate) {
      batchWarning = Strings.deviceInvoices.batchActionAllActionRequiredWarning;
    } else if (invoicesToProcess.length === 1) {
      batchWarning = Strings.deviceInvoices.batchActionSingleWarning;
    }
    const title = isBulkRecalculate ? Strings.deviceInvoices.recalculateAllActionRequiredInvoicesWarning : Strings.deviceInvoices.recalculateInvoicesWarning;
    const caption = isBulkRecalculate || invoicesToProcess.length > 1 ? Strings.deviceInvoices.recalculateInvoices : Strings.deviceInvoices.recalculateInvoice;

    const modalData = {
      title,
      batchWarning,
      onConfirmAction: () => recalculateAction(payload).then(resp => {
        const expectedType = isRecurring ? RECALCULATE_DEVICE_INVOICE_RECURRING_RESULT : RECALCULATE_DEVICE_INVOICE_INITIAL_RESULT;
        if (resp.type === expectedType) {
          showNotification(getGenerationSummaryMessage(resp.response), 5000, false);
        }
        return resp;
      }),
      confirmPostAction: loadData,
      caption,
      hideCaution: true,
      onCancelAction: null,
    };
    openConfirmModal(modalData);
  };

  const handleGenerateRecurring = month => {
    if (!month) {
      showNotification(Strings.deviceInvoicesRecurringSingleMonthRequired, 4000, true);
      return Promise.resolve(false);
    }

    const selectedBillingMonth = moment(month).startOf('month');
    if (selectedBillingMonth.isAfter(moment().startOf('month'))) {
      showNotification(Strings.deviceInvoices.recurringStartedMonthRequired || 'Select current or past month.', 4000, true);
      return Promise.resolve(false);
    }

    const payload = {billing_month: selectedBillingMonth.format('YYYY-MM')};

    return generateDeviceInvoiceRecurring(payload)
      .then(resp => {
        if (resp.type === GENERATE_DEVICE_INVOICE_RECURRING_RESULT) {
          showNotification(getGenerationSummaryMessage(resp.response), 5000, false);
          setShowGenerateRecurringModal(false);
          loadData();
          return true;
        }
        showNotification(extractErrorMessage(resp), 5000, true);
        setIsLoading(false);
        return false;
      })
      .catch(error => {
        showNotification(extractExceptionMessage(error), 5000, true);
        setIsLoading(false);
        return false;
      });
  };
 
  const onDownloadBtnClickSuccessHandler = (req, filename) => {
    const fileName =
      req?.response && req?.response?.name ? req?.response?.name : filename || moment().toString();
    downloadFileOnly(req, fileName);
  };

  const downloadData = () => {
    const onDateSubmited = dates => {
      const request = {
        startTimestamp: dates.startDate ? moment(dates.startDate * 1000).format() : undefined,
        endTimestamp: dates.endDate ? moment(dates.endDate * 1000).format() : undefined,
      };
      const filenamePrefix = isRecurring ? 'device_invoices_recurring' : 'device_invoices_initial';
      const downloadDates = request.startTimestamp && request.endTimestamp 
        ? `_${moment(request.startTimestamp).format('YYYYMMDD')}_${moment(request.endTimestamp).format('YYYYMMDD')}`
        : `_${moment().format('YYYYMMDD')}`;
      const action = isRecurring ? downloadDeviceInvoiceRecurring : downloadDeviceInvoiceInitial;
      action(request).then(resp => {
        if (resp.type === DOWNLOAD_DEVICE_INVOICE_INITIAL_RESULT || resp.type === DOWNLOAD_DEVICE_INVOICE_RECURRING_RESULT) {
          onDownloadBtnClickSuccessHandler(resp, `${filenamePrefix}${downloadDates}`);
        }
      });
    };
    openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.downloadData,
      text: isRecurring
        ? Strings.deviceInvoices.recurringDownloadDateRangeInfo
        : Strings.deviceInvoices.initialDownloadDateRangeInfo,
      submitButtonText: Strings.download,
    });
  };
  const options = React.useMemo(() => {
    if (!canManageDeviceInvoices) {
      return [];
    }
    const ret = [];
    if (isRecurring) {
      ret.push({ onSelect: () => setShowGenerateRecurringModal(true), label: Strings.deviceInvoices.generateInvoicesForMonth });      
    }
    if (status === INVOICE_STATUSES.action_required.key) {
      ret.push({ onSelect: () => handleRecalculate(), label: Strings.deviceInvoices.recalculateInvoices });
    }
    ret.push({ onSelect: () => downloadData(), label: Strings.downloadData });
    return ret;
  }, [canManageDeviceInvoices, status, isRecurring]);

  const billingMonthDatePickerProps = useMemo(() => ({
    maxDate: moment().endOf('month'),
    openTo: "month",
    views: ['year', 'month'],
    actions: [],
  }), []);
  const monthFilterLabel = isRecurring ? Strings.deviceInvoices.billingMonth : (Strings.shipmentDate || 'Shipment Date');

  const orgsForFilter = useMemo(() => {
    if (hasBillingOrgScope && showOnlyMyBillingOrgs) {
      return currentUserBillingOrgs;
    }
    return orgs;
  }, [currentUserBillingOrgs, hasBillingOrgScope, orgs, showOnlyMyBillingOrgs]);
  const billingOrgsForFilter = useMemo(() => {
    if (hasBillingOrgScope && showOnlyMyBillingOrgs) {
      return currentUserBillingOrgs;
    }
    return orgs;
  }, [currentUserBillingOrgs, hasBillingOrgScope, orgs, showOnlyMyBillingOrgs]);

  const getTabRightContent = useCallback(() => {
    return (
      <>
        {hasBillingOrgScope && (
          <Input
            type="checkbox"
            id="show-only-my-billing-orgs"
            checked={showOnlyMyBillingOrgs}
            onChange={e => setShowOnlyMyBillingOrgs(e.target.checked)}
            label="Show only my billing orgs"
            rowClassName="checkbox-row"
          />
        )}
        <TextInput placeholder={Strings.search} onChange={e => setSearchQuery(e.target.value)} value={searchQuery} />
        <AdvancedFilters>
          <Filter definition={MonthFilter(monthFilterLabel, billingMonthDatePickerProps).month} />
          {orgsForFilter?.length > 0 && <Filter definition={OrgFilter(orgsForFilter, false, 'customer_id', Strings.customer).customer_id} />}
          {billingOrgsForFilter?.length > 0 && <Filter definition={OrgFilter(billingOrgsForFilter, false, 'billing_org_id', Strings.deviceInvoices.billingOrg).billing_org_id} />}
          {careProviders?.length > 0 && <Filter definition={CareProviderFilter(careProviders).provider} />}
          {!isRecurring && <Filter definition={getFiltersDefinition({ includeHub: true }).DeviceType} />}
          {isRecurring && <Filter definition={getFiltersDefinition().programCheckbox} />}
        </AdvancedFilters>
        {canManageDeviceInvoices && (
          <DropdownMenu
            title={Strings.actions}
            options={options}
          />
        )}
      </>
    );
  }, [billingMonthDatePickerProps, billingOrgsForFilter, canManageDeviceInvoices, careProviders, hasBillingOrgScope, isRecurring, monthFilterLabel, options, orgsForFilter, searchQuery, showOnlyMyBillingOrgs]);

  const onOffsetChange = useCallback(offset => {
    if (isNaN(offset)) return;
    setPagination(prev => ({ ...prev, offset }));
  }, []);

  const onPageSizeChange = useCallback(limit => {
    setPagination(prev => ({ ...prev, offset: 0, limit }));
  }, []);

  const onSortingChange = useCallback(nextSorting => {
    const normalizedSorting = nextSorting || [];
    if (isEqual(normalizedSorting, sorting)) {
      return;
    }
    setSorting(normalizedSorting);
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, [sorting]);

  const tabs = useMemo(() => DeviceInvoicesTabsDefinition.map(tab => (
    <Tab
      key={tab.key}
      label={tab.label}
      count={tabCounts[tab.key]}
    >
      <AdvancedFiltersBar
        customFilters={{
          ...OrgFilter(orgsForFilter, false, 'customer_id', Strings.customer),
          ...OrgFilter(billingOrgsForFilter, false, 'billing_org_id', Strings.deviceInvoices.billingOrg),
          ...MonthFilter(monthFilterLabel, billingMonthDatePickerProps),
          ...CareProviderFilter(careProviders),
          ...invoiceDateFilter,
          ...(!isRecurring ? { DeviceType: getFiltersDefinition({ includeHub: true }).DeviceType } : {}),
        }}
      />
      <DeviceInvoicesReviewTable
        data={tab.key === status ? data : []}
        isLoading={tab.key === status && isLoading}
        loadData={loadData}
        tableName={`device-invoices-${invoiceType}-${tab.key}`}
        isRecurring={isRecurring}
        orgs={currentUserBillingOrgs}
        status={tab.key}
        handleRecalculate={handleRecalculate}
        pagination={tab.key === status ? pagination : { offset: 0, limit: pagination.limit, totalRecords: tabCounts[tab.key] || 0 }}
        sorting={tab.key === status ? sorting : getDefaultSorting(invoiceType, tab.key)}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
      />
    </Tab>
  )), [
    billingMonthDatePickerProps,
    careProviders,
    currentUserBillingOrgs,
    data,
    handleRecalculate,
    invoiceDateFilter,
    invoiceType,
    isLoading,
    isRecurring,
    loadData,
    monthFilterLabel,
    onOffsetChange,
    onPageSizeChange,
    onSortingChange,
    billingOrgsForFilter,
    orgsForFilter,
    pagination,
    sorting,
    status,
    tabCounts,
  ]);

  return (
    <>
      <Tabs
        defaultIndex={DeviceInvoicesTabsDefinition.findIndex(t => t.key === status)}
        onChange={i => setStatus(DeviceInvoicesTabsDefinition[i].key)}
        rightContent={getTabRightContent()}
      >
        {tabs}
      </Tabs>
      {showGenerateRecurringModal && (
        <GenerateRecurringInvoicesModal
          onClose={() => setShowGenerateRecurringModal(false)}
          onSubmit={handleGenerateRecurring}
        />
      )}
    </>
  );
}

DeviceInvoicesTabs.propTypes = {
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  invoiceType: PropTypes.string.isRequired,
  refreshTimestamp: PropTypes.any,
  replacePath: PropTypes.func.isRequired,
  getDeviceInvoiceInitialRows: PropTypes.func.isRequired,
  getDeviceInvoiceRecurringRows: PropTypes.func.isRequired,
  recalculateDeviceInvoiceInitial: PropTypes.func.isRequired,
  recalculateDeviceInvoiceRecurring: PropTypes.func.isRequired,
  generateDeviceInvoiceRecurring: PropTypes.func.isRequired,
  downloadDeviceInvoiceInitial: PropTypes.func.isRequired,
  downloadDeviceInvoiceRecurring: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  getOrgs: PropTypes.func.isRequired,
  getCareProviders: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  openDateRangeSelectionModal: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  replacePath: url => dispatch(replace(url)),
  getDeviceInvoiceInitialRows: params => dispatch(invoiceActions.getDeviceInvoicesInitial(params)),
  getDeviceInvoiceRecurringRows: params => dispatch(invoiceActions.getDeviceInvoicesRecurring(params)),
  recalculateDeviceInvoiceInitial: payload => dispatch(invoiceActions.recalculateDeviceInvoiceInitial(payload)),
  recalculateDeviceInvoiceRecurring: payload => dispatch(invoiceActions.recalculateDeviceInvoiceRecurring(payload)),
  generateDeviceInvoiceRecurring: payload => dispatch(invoiceActions.generateDeviceInvoiceRecurring(payload)),
  downloadDeviceInvoiceInitial: params => dispatch(invoiceActions.downloadDeviceInvoiceInitial(params)),
  downloadDeviceInvoiceRecurring: params => dispatch(invoiceActions.downloadDeviceInvoiceRecurring(params)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
  getOrgs: request => dispatch(orgFilterActions.getMyOrgs(request)),
  getCareProviders: request => dispatch(organizationActions.getCareProviders(request)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
});

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
  authConfig: state.auth.config,
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceInvoicesTabs);
